import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const CookiePolicyPage = () => {
  return (
    <div className="min-h-screen bg-gray-950 pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Link */}
        <Link to="/" className="inline-flex items-center text-gray-400 hover:text-white mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        <h1 className="text-4xl font-bold text-white mb-8">Cookie Policy</h1>
        <p className="text-gray-400 mb-8">Last updated: February 11, 2026</p>

        <div className="prose prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">1. What Are Cookies</h2>
            <p className="text-gray-300 leading-relaxed">
              Cookies are small text files that are stored on your computer or mobile device when you visit 
              our website. They are widely used to make websites work more efficiently and provide information 
              to website owners.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">2. How We Use Cookies</h2>
            <p className="text-gray-300 leading-relaxed mb-4">Life Decoder uses cookies for the following purposes:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong className="text-white">Essential Cookies:</strong> Required for the website to function properly, including user authentication and session management</li>
              <li><strong className="text-white">Analytics Cookies:</strong> Help us understand how visitors interact with our website</li>
              <li><strong className="text-white">Functional Cookies:</strong> Remember your preferences and settings</li>
              <li><strong className="text-white">Marketing Cookies:</strong> Track your activity to deliver relevant advertisements</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">3. Types of Cookies We Use</h2>
            
            <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-6 mb-4">
              <h3 className="text-lg font-semibold text-amber-400 mb-2">Essential Cookies</h3>
              <p className="text-gray-300 text-sm mb-2">These cookies are necessary for the website to function and cannot be switched off.</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left py-2 text-gray-400">Cookie Name</th>
                    <th className="text-left py-2 text-gray-400">Purpose</th>
                    <th className="text-left py-2 text-gray-400">Duration</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  <tr className="border-b border-gray-800">
                    <td className="py-2">session_token</td>
                    <td className="py-2">User authentication</td>
                    <td className="py-2">7 days</td>
                  </tr>
                  <tr>
                    <td className="py-2">csrf_token</td>
                    <td className="py-2">Security</td>
                    <td className="py-2">Session</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-6 mb-4">
              <h3 className="text-lg font-semibold text-amber-400 mb-2">Analytics Cookies</h3>
              <p className="text-gray-300 text-sm mb-2">These cookies help us understand how visitors use our website.</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left py-2 text-gray-400">Cookie Name</th>
                    <th className="text-left py-2 text-gray-400">Purpose</th>
                    <th className="text-left py-2 text-gray-400">Duration</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  <tr className="border-b border-gray-800">
                    <td className="py-2">_ga</td>
                    <td className="py-2">Google Analytics</td>
                    <td className="py-2">2 years</td>
                  </tr>
                  <tr>
                    <td className="py-2">_gid</td>
                    <td className="py-2">Google Analytics</td>
                    <td className="py-2">24 hours</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">4. Managing Cookies</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              You can control and manage cookies in various ways. Please note that removing or blocking 
              cookies may impact your user experience and some functionality may no longer be available.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong className="text-white">Browser Settings:</strong> Most browsers allow you to refuse 
              or accept cookies, delete existing cookies, and set preferences for certain websites.
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300">Google Chrome</a></li>
              <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300">Mozilla Firefox</a></li>
              <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300">Safari</a></li>
              <li><a href="https://support.microsoft.com/en-us/windows/delete-and-manage-cookies-168dab11-0753-043d-7c16-ede5947fc64d" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300">Microsoft Edge</a></li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">5. Third-Party Cookies</h2>
            <p className="text-gray-300 leading-relaxed">
              Some cookies are placed by third-party services that appear on our pages. We do not control 
              the dissemination of these cookies. You should check the third-party websites for more 
              information about these cookies.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">6. Updates to This Policy</h2>
            <p className="text-gray-300 leading-relaxed">
              We may update this Cookie Policy from time to time. Any changes will be posted on this page 
              with an updated revision date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">7. Contact Us</h2>
            <p className="text-gray-300 leading-relaxed">
              If you have questions about our use of cookies, please contact us at:
            </p>
            <p className="text-gray-300 mt-4">
              <strong className="text-white">Life Decoder</strong><br />
              Email: team@digitaldivecoaching.com<br />
              Address: No. H-8-2, Block H, Setiawalk Puchong, 47160 Selangor
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;
